// src/components/features/journey-planning/ResultsList/JourneyPlanResultsList.tsx
import type { ComponentProps } from 'preact';
import { CardBackdrop } from '../../../base/CardBackdrop/CardBackdrop';
import type { JourneyPlan } from '../../../../arcticapi/types';
import { JourneyPlanItinerarySummary } from '../ItinerarySummary/JourneyPlanItinerarySummary';
import { buildPlanKey, getItineraryLocalStartDate } from '../formatters';
import { Button } from '../../../base/Button/Button';
import { Flex } from '../../../base/Flex/Flex';
// styles
import styles from './JourneyPlanResultsList.module.css';

export interface JourneyPlanResultsListProps extends Omit<
  ComponentProps<'section'>,
  'children' | 'className'
> {
  plans: JourneyPlan[];
  onSelectPlan: (index: number) => void;
  onShowEarlierJourneys?: () => void;
  onShowLaterJourneys?: () => void;
  searchedDate?: string | null;
}

/**
 * Lists multiple journey plan options returned by search.
 *
 * Intended to hold several JourneyPlanItinerarySummary items.
 */
export function JourneyPlanResultsList({
  plans,
  onSelectPlan,
  onShowEarlierJourneys,
  onShowLaterJourneys,
  searchedDate = null,
  ...props
}: JourneyPlanResultsListProps) {
  if (!plans.length) return null;

  return (
    <CardBackdrop scroll="y">
      {onShowEarlierJourneys && (
        <Flex direction="row" justify="center" mb="base">
          <Button
            type="button"
            variant="secondary"
            onClick={onShowEarlierJourneys}
          >
            Earlier journeys
          </Button>
        </Flex>
      )}
      <section className={styles.resultsList} {...props}>
        {plans.map((plan, index) => {
          const itineraryStartDate = getItineraryLocalStartDate(plan);
          const isSameDay =
            searchedDate !== null &&
            itineraryStartDate !== null &&
            itineraryStartDate === searchedDate;

          return (
            <JourneyPlanItinerarySummary
              key={buildPlanKey(plan, index)}
              plan={plan}
              index={index}
              onSelectPlan={onSelectPlan}
              searchedDate={searchedDate}
              isSameDay={isSameDay}
            />
          );
        })}
      </section>
      {onShowLaterJourneys && (
        <Flex direction="row" justify="center" mt="base">
          <Button
            type="button"
            variant="secondary"
            onClick={onShowLaterJourneys}
          >
            Later journeys
          </Button>
        </Flex>
      )}
    </CardBackdrop>
  );
}
